import * as moment from "moment";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import app from "../../core/application";
import BackButton from "../../core/BackButton/BackButton";
import * as Constants from "../../core/constants";
import ExportButton from "../../core/ExportButton/ExportButton";
import Spinner from "../../core/Spinner/Spinner";
import TableCheckboxListFilter, { ICheckboxValue } from "../../core/TableCheckboxListFilter/TableCheckboxListFilter";
import TableDateRangeFilter from "../../core/TableDateRangeFilter/TableDateRangeFilter"
import TableTextFilter from "../../core/TableTextFilter/TableTextFilter";
import debounce from "../../helpers/debounce";
import { sessionGetItem, sessionSetItem } from "../../helpers/storage";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IListResults from "../../types/IListResults";
import IMessageHeaderSummary from "../../types/IMessageHeader";
import IMessageSearchRequest from "../../types/IMessageSearchRequest";
import IResponse from "../../types/IResponse";
import "./MessageList.css";
import MessageRow from "./MessageRow";

interface IMessageListPersistedState {
    messageText: string;
    startDate: string;
    endDate: string;
    messageTypes: string[];
}

interface IMessageListProps extends ITranslationProps, RouteComponentProps<any> {
    loading: boolean;
    search: (request: IMessageSearchRequest) => Promise<IResponse<IListResults<IMessageHeaderSummary>> | null>;
}

interface IMessageListState {
    messageText: string;
    startDate: Date;
    endDate: Date;
    messageTypes: string[];
    page: number;
    moreRecordsExist: boolean;
    messages: IMessageHeaderSummary[];
}

class MessageList extends React.Component<IMessageListProps, IMessageListState> {
    private exportForm: HTMLFormElement | null;

    private searchWithDebounce = debounce(() => { this.search(); }, 500);

    private messageTypeValues: ICheckboxValue[] = [
        { key: Constants.MESSAGE_TYPE_EMAIL, value: Constants.MESSAGE_TYPE_EMAIL },
        { key: Constants.MESSAGE_TYPE_PUSH, value: Constants.MESSAGE_TYPE_PUSH },
        { key: Constants.MESSAGE_TYPE_SMS, value: Constants.MESSAGE_TYPE_SMS },
        { key: Constants.MESSAGE_TYPE_VOICE, value: Constants.MESSAGE_TYPE_VOICE },
    ];

    private stateStorageKey: string = "messageList_state_storage";
    private defaultStartDate: Date = moment().hour(0).minute(0).second(0).millisecond(0).add(-1, "month").toDate();
    private defaultEndDate: Date = moment().hour(0).minute(0).second(0).millisecond(0).toDate();

    constructor(props: IMessageListProps) {
        super(props);

        this.state = {
            messageText: "",
            startDate: this.defaultStartDate,
            endDate: this.defaultEndDate,
            messageTypes: [],
            page: 1,
            moreRecordsExist: false,
            messages: []

        };

        this.search = this.search.bind(this);
        this.loadNextPage = this.loadNextPage.bind(this);
        this.updateMessageText = this.updateMessageText.bind(this);
        this.updateDateRange = this.updateDateRange.bind(this);
        this.updateMessageTypes = this.updateMessageTypes.bind(this);
        this.onRowClick = this.onRowClick.bind(this);
        this.onExportClick = this.onExportClick.bind(this);
    }

    public componentDidMount() {
        const savedState = sessionGetItem<IMessageListPersistedState>(this.stateStorageKey);

        if (savedState) {
            this.setState({
                messageText: savedState.messageText,
                messageTypes: savedState.messageTypes,
                startDate: moment(savedState.startDate).toDate(),
                endDate: moment(savedState.endDate).toDate()
            }, () => { this.search(true) });
        } else {
            this.search(true);
        }
    }

    public componentWillUnmount() {

        const persistedState: IMessageListPersistedState = {
            messageText: this.state.messageText,
            messageTypes: this.state.messageTypes,
            startDate: this.state.startDate.toJSON(),
            endDate: this.state.endDate.toJSON()
        };

        sessionSetItem(this.stateStorageKey, persistedState);
    }

    public render() {
        const application = app();
        const dlToken = application.userSession ? application.userSession.fileDownloadAuthenticationToken : "";

        const messageRows: React.ReactNode[] = [];
        this.state.messages.forEach(msg => {
            messageRows.push(<MessageRow key={msg.id} {...msg} onclick={this.onRowClick} />);
        });

        const messageTypeInputs: React.ReactNode[] = [];
        this.state.messageTypes.forEach((messageType, index) => {
            messageTypeInputs.push(<input key={index} type="hidden" name="messageTypes[]" value={messageType} />);
        });

        return (
            <div>
                {this.props.loading && <Spinner />}

                <BackButton />
                <h2>{this.props.t("MessageList.Title")}</h2>

                <div className="float-right">
                    <ExportButton onClick={this.onExportClick} />
                    <form ref={ref => this.exportForm = ref} method="POST" style={{ "display": "none" }}
                        action={`${application.webServiceBaseURL}/api/messages/exportmessages?dltoken=${dlToken}`}
                         target="_blank">
                        <input type="hidden" name="Text" value={this.state.messageText} />
                        <input type="hidden" name="StartDate" value={this.state.startDate.toJSON()} />
                        <input type="hidden" name="EndDate" value={this.state.endDate.toJSON()} />
                        {messageTypeInputs}
                    </form>
                </div>

                <table className="table table-sm messagelist-table">
                    <thead className="thead-light">
                        <tr>
                            <th>
                                <div>{this.props.t("MessageList.MessageText")}</div>
                                <TableTextFilter onChange={this.updateMessageText} searchButtonClick={this.search} value={this.state.messageText} />
                            </th>
                            <th style={{ width: "40%" }}>
                                <div>{this.props.t("MessageList.MessageDate")}</div>
                                <TableDateRangeFilter startDate={this.state.startDate} endDate={this.state.endDate} onChange={this.updateDateRange} />
                            </th>
                            <th style={{ width: "15%" }}>
                                <div>{this.props.t("MessageList.MessageType")}</div>
                                <TableCheckboxListFilter values={this.messageTypeValues} onChange={this.updateMessageTypes} selectedValues={this.state.messageTypes} />
                            </th>
                        </tr>
                    </thead>
                </table>
                <table className="table table-sm messagelist-table table-hover">
                    <tbody>
                        <tr>
                            <th>{this.props.t("MessageList.ReferenceID")}</th>
                            <th>{this.props.t("MessageList.Description")}</th>
                            <th>{this.props.t("MessageList.Content")}</th>
                            <th style={{ width: "150px" }}>{this.props.t("Global.Date")}</th>
                            <th>{this.props.t("Global.Type")}</th>
                        </tr>
                        {messageRows}
                    </tbody>
                </table>
                {
                    this.state.moreRecordsExist &&
                    <button data-id="showMoreButton" className="btn btn-outline-secondary" onClick={this.loadNextPage}>{this.props.t("Global.ShowMore")}
                    </button>
                }
            </div>
        );
    }

    private loadNextPage() {
        this.setState({ page: this.state.page + 1 }, () => { this.search(false) });
    }

    private search(newSearch: boolean = true) {

        this.setState({ page: newSearch ? 1 : this.state.page }, async () => {
            const response = await this.props.search(this.getSearchRequest());

            if (response && response.success) {
                if (newSearch) {
                    this.setState({ messages: response.payload.records, moreRecordsExist: response.payload.moreRecordsExist });
                } else {
                    this.setState((state: IMessageListState) => {
                        state.messages.push(...response.payload.records);
                        state.moreRecordsExist = response.payload.moreRecordsExist;
                        return state;
                    });
                }
            }
        });
    }

    private updateMessageText(text: string) {
        this.setState({ messageText: text, page: 1 }, this.searchWithDebounce);
    }

    private updateMessageTypes(selectedItems: string[]) {
        this.setState({ messageTypes: selectedItems }, this.searchWithDebounce);
    }

    private updateDateRange(newStartDate: Date | null, newEndDate: Date | null) {

        const startDate = newStartDate ? newStartDate : this.defaultStartDate;
        const endDate = newEndDate ? newEndDate : this.defaultEndDate;

        if (startDate !== this.state.startDate || endDate !== this.state.endDate) {
            this.setState({ startDate, endDate, page: 1 }, this.searchWithDebounce);
        }
    }

    private getSearchRequest(): IMessageSearchRequest {
        const request: IMessageSearchRequest = {
            text: this.state.messageText,
            page: this.state.page,
            messageTypes: this.state.messageTypes,
            startDate: this.state.startDate.toJSON(),
            endDate: this.state.endDate.toJSON()
        };
        return request;
    }

    private onRowClick(messageID: number, messageType: string) {
        this.props.history.push(`/message/${messageID}/${messageType}`);
    }

    private onExportClick() {
        if (this.exportForm) {
            this.exportForm.submit();
        }
    }
}

export default withRouter(translateComponent(MessageList));



// WEBPACK FOOTER //
// ./src/components/MessageList/MessageList.tsx